import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref } from "vue"

import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import { useDataSources } from '@/composables/useDataSources'
import { useFeature } from '@/pinia/features'
import { useButtonTextFormatter } from '@/components/AnalyticsDashboard/components/CollapseController/composables'
import { LOCALES } from '@/utils/locales'
import usePdfReport from '@/components/AnalyticsDashboard/usePdfReport'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import AnimatedBar from './AnimatedBar.vue'
import IconSmallInfo from '../assets/icons/IconSmallInfo.vue'
import TooltipStandardV2 from '../atoms/TooltipStandardV2.vue'
import TextBase from '../atoms/TextBase.vue'
import { useSelectionStore } from '@/pinia/selectionNew'

type Props = {
  title: string
  fullscreen?: boolean
  showResizeButton?: boolean
  showBackButton?: boolean
  info?: string
}
type Emits = {
  (e: 'exit'): void
  (e: 'toggleFullscreen'): void
  (e: 'goBack'): void
}

export default /*@__PURE__*/_defineComponent({
  __name: 'FeatureControlSection',
  props: {
    title: {},
    fullscreen: { type: Boolean },
    showResizeButton: { type: Boolean },
    showBackButton: { type: Boolean },
    info: {}
  },
  emits: ["exit", "toggleFullscreen", "goBack"],
  setup(__props: any) {

const props = __props


const piniaStore = useGeneralStore()
const store = useStore()
const { locale } = useI18n()
const { buttonText: dateInLocaleString } = useButtonTextFormatter(LOCALES[locale.value])
const { dataSources } = storeToRefs(useDataSources())
const { feature: currentSection } = useFeature('home/*/*/*/data/*')
const selectionStore = useSelectionStore()
const { generatePdfReport } = usePdfReport()

const allBuildings = computed<Object[]>(() => store.getters.allBuildings)
const clickedItemType = computed<string>(() => store.state.clickedItemType)
const enablePrintReport = computed<boolean>(
  () => currentSection.value?.parent?.parent?.name !== undefined
)
const realmLogo = computed<string>(() => store.getters.realmLogo)
const title = computed<string>(() => props.title.replace('&shy;', ''))

function printReport() {
  const kpis = currentSection.value?.children
    ?.filter((c) => c.component === 'MainMeasurementCard')
    .map((c) => ({
      dataSource: dataSources.value[c.props.value.dataSource],
      config: c
    }))
  const coloredEntities = currentSection.value?.children
    ?.filter((c) => c.component === 'MainMeasurementCard')
    .map((c) => ({
      dataSource: dataSources.value[c.props.coloredEntities.dataSource],
      config: c
    }))
  const entitySourceBuildings = allBuildings.value
  const entitySourceLevels = store.state.levelsForSelectedBuilding?.data?.features ?? []
  const entitySourceRooms = store.state.roomsForSelectedLevel?.data?.features ?? []
  const entitySource = entitySourceBuildings.concat(entitySourceLevels).concat(entitySourceRooms)
  const titleReport = `${currentSection.value?.parent?.parent?.title[locale.value].replaceAll(
    '&shy;',
    ''
  )} - ${currentSection.value?.title[locale.value].replaceAll('&shy;', '')}`
  generatePdfReport(
    titleReport ?? '',
    currentSection.value?.parent?.parent?.name,
    selectionStore.entityPath,
    dateInLocaleString.value,
    kpis,
    piniaStore.selectedAnalysisKPI,
    coloredEntities,
    entitySource,
    clickedItemType.value,
    realmLogo.value
  )
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AnimatedBar, { showLeft: _ctx.showBackButton }, {
    left: _withCtx(() => [
      _createVNode(ButtonText, {
        id: "feature-navigate-back",
        class: "text-button",
        iconLeft: require('@/design-system/assets/icons/Icon-Chevron-Left.svg'),
        size: "extra-small",
        onOnClicked: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('goBack')))
      }, null, 8, ["iconLeft"])
    ]),
    middle: _withCtx(() => [
      _createVNode(TextBase, {
        text: title.value.replace('­', ''),
        type: "h4",
        weight: "bold"
      }, null, 8, ["text"]),
      (_ctx.info)
        ? (_openBlock(), _createBlock(TooltipStandardV2, {
            key: 0,
            style: 'margin-left: 5px; margin-top: 5px;',
            text: _ctx.info,
            arrowPosition: !_ctx.fullscreen ? 'top-center' : 'center-right',
            verticalOffset: !_ctx.fullscreen ? 4 : 1,
            horizontalOffset: !_ctx.fullscreen ? 0 : 2
          }, {
            default: _withCtx(() => [
              _createVNode(IconSmallInfo, { class: "icon" })
            ]),
            _: 1
          }, 8, ["text", "arrowPosition", "verticalOffset", "horizontalOffset"]))
        : _createCommentVNode("", true)
    ]),
    right: _withCtx(() => [
      (_unref(piniaStore).showFeatureAnalytic && enablePrintReport.value)
        ? (_openBlock(), _createBlock(ButtonText, {
            key: 0,
            id: "button-create-report",
            class: "text-button",
            iconLeft: require('@/design-system/assets/icons/Icon-File-Text-Small-Blue.svg'),
            size: "extra-small",
            onOnClicked: printReport
          }, null, 8, ["iconLeft"]))
        : _createCommentVNode("", true),
      (_ctx.showResizeButton)
        ? (_openBlock(), _createBlock(ButtonText, {
            key: 1,
            id: "feature-toggle-fullscreen",
            class: "text-button",
            iconLeft: 
          _ctx.fullscreen
            ? require('@/design-system/assets/icons/Icon-Minimize-2.svg')
            : require('@/design-system/assets/icons/Icon-Maximize-2.svg')
        ,
            size: "extra-small",
            onOnClicked: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('toggleFullscreen')))
          }, null, 8, ["iconLeft"]))
        : _createCommentVNode("", true),
      _createVNode(ButtonText, {
        id: "feature-exit",
        class: "text-button",
        iconLeft: require('@/design-system/assets/icons/Icon-X-Small.svg'),
        size: "extra-small",
        onOnClicked: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('exit')))
      }, null, 8, ["iconLeft"])
    ]),
    _: 1
  }, 8, ["showLeft"]))
}
}

})